/**
 * Company: SPHMedia
 * Description: [Fetches recommended articles for myBH]
 */

import { UserToken } from "@app/store/useUserVarStore";
import {
  fetchArticlesByPaths,
  getMoreChartBeatRecommendedArticleFeeds,
} from "@helper/getChartBeatRecommendation";
import { useCallback, useEffect, useState } from "react";

import { ProcessedArticleData } from "./transformer/useOSResponse";

type useChartBeatRecommendedArticlesData = {
  articles: ProcessedArticleData[];
  loading: boolean;
  error: boolean;
  refetch: () => Promise<void>;
};

type useChartBeatRecommendedArticlesProps = {
  userToken: UserToken;
  deviceId: string | null;
  page?: number;
  size?: number;
  sections?: string[];
  enabled?: boolean;
  initialArticles?: ProcessedArticleData[]; // Optional initial articles to populate the state
};

/**
 * Custom React Hook: useChartBeatRecommendedArticlesHook
 * Description: [Fetches recommended articles based on user id or device id for myBH recommended page]
 * @param props - The props required by the hook (if any)
 * @returns data - The data returned by the hook
 */
export default function useChartBeatRecommendedArticles({
  userToken,
  deviceId,
  page = 1,
  size,
  sections,
  enabled = true,
  initialArticles = [],
}: useChartBeatRecommendedArticlesProps): useChartBeatRecommendedArticlesData {
  const [articles, setArticles] =
    useState<ProcessedArticleData[]>(initialArticles);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const fetchArticles = useCallback(async () => {
    setLoading(true);
    setError(false);

    try {
      // Step 1: get paths
      const { articlePaths } = await getMoreChartBeatRecommendedArticleFeeds(
        userToken,
        deviceId,
        page,
        size,
        sections,
      );

      if (articlePaths.length === 0) {
        return;
      }

      // Step 2: fetch articles
      const articlesData = await fetchArticlesByPaths(articlePaths);

      setArticles(articlesData);
    } catch (err) {
      console.error("Failed to fetch recommended articles", err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [userToken, deviceId, page, size, sections]);

  // initial + dependency change fetch
  useEffect(() => {
    if (!enabled) return;

    void fetchArticles();
  }, [enabled, fetchArticles]);

  return {
    articles,
    loading,
    error,
    refetch: fetchArticles,
  };
}
